import { Location } from '@angular/common'
import { Injectable } from '@angular/core'
import { Sort } from '@angular/material/sort'
import {
  INew_TableAction,
  ITimePeriodRequest,
  ITimePeriodWithLocationIds,
  IUrlFilterItem,
  New_TableFilters,
  New_TableOperator,
  SignUrlService
} from '@lla-platform/core/core-data-access'
import { TABLE_CONSTANTS } from '@lla-platform/core/core-util'
import { pickBy } from 'lodash'
import { ChapterCodeType, InvoiceOperatedType } from '@lla-platform/receivable/receivable-data-access'

/** Period, locations and return page every list drill-down is scoped to. */
export interface IDrillDownScope {
  period: ITimePeriodRequest
  locationIds?: string[]
  /** Page to return to; may carry its own `?filters=` token so the back link restores it. */
  returnUrl: string
}

export interface ICategoryDrillDown extends IDrillDownScope {
  categoryIds: (string | undefined)[]
}

export interface IEmployeeEfficiencyDrillDown {
  employeeId: string
  firstName: string
  lastName: string
  /** Front shop (service advisors, managers) opens the service-advisor list; everyone else the technician list. */
  frontShop: boolean
  period: ITimePeriodRequest
  locationIds: string[]
}

const LIST_SORT: Sort = { active: 'invoiceDate', direction: 'desc' }
const EFFICIENCY_LIST_SORT: Sort = { active: 'technicianEfficiency', direction: 'desc' }

/**
 * Query params for the list pages summary cells drill into, built the one way each list expects so
 * every caller (tracker, summaries, reports) reconciles with the same rows.
 */
@Injectable({
  providedIn: 'root'
})
export class DrillDownService {
  constructor(
    private signUrlService: SignUrlService,
    private location: Location
  ) {}

  /**
   * The current page as a return URL, `?filters=` included. SetFilters keeps that token in sync through
   * Location.replaceState, which Router.url never sees, so the back link restores this page's filters.
   */
  currentPageReturnUrl(): string {
    return this.location.path()
  }

  /** Scope for a summary row: Grand Total drills into the filter's locations, a location row into its own. */
  rowScope(
    rowLocationId: string | undefined,
    filter: ITimePeriodWithLocationIds,
    returnUrl: string
  ): IDrillDownScope {
    const locationIds =
      rowLocationId === TABLE_CONSTANTS.GRAND_TOTAL_ID
        ? (filter.locationIds ?? [])
        : rowLocationId
          ? [rowLocationId]
          : []
    return { period: filter, locationIds, returnUrl }
  }

  /** Invoices overview scoped to the period and locations. */
  invoicesListOverview(scope: IDrillDownScope): IUrlFilterItem {
    return this.signUrlService.generateFilters({
      invoicesListFilters: { filters: this.scopeFilters(scope), page: 1, sort: LIST_SORT },
      invoicesListReturnUrl: scope.returnUrl
    })
  }

  /** GP package list scoped to the period and locations. */
  gpPackageList(scope: IDrillDownScope): IUrlFilterItem {
    return this.signUrlService.generateFilters({
      gpPackageListFilters: { filters: this.scopeFilters(scope), page: 1, sort: LIST_SORT },
      gpPackageListReturnUrl: scope.returnUrl
    })
  }

  /** GP package list narrowed to service categories: the tracker's category cells recipe. */
  gpPackageListByCategory(input: ICategoryDrillDown): IUrlFilterItem {
    const filters: New_TableFilters = {
      ...this.scopeFilters(input),
      ...this.equalsFilters({
        serviceCategoryId: input.categoryIds,
        chapterCode: [
          ChapterCodeType.Service,
          ChapterCodeType.Inspection,
          ChapterCodeType.Concern,
          ChapterCodeType.GeneralCredits
        ]
      })
    }
    return this.signUrlService.generateFilters({
      gpPackageListFilters: { filters, page: 1, sort: LIST_SORT },
      gpPackageListReturnUrl: input.returnUrl
    })
  }

  /** Tires list scoped to the period and locations, narrowed by column → value equality filters. */
  tiresList(
    scope: IDrillDownScope,
    equals: Record<string, unknown> = {},
    sort: Sort = LIST_SORT
  ): IUrlFilterItem {
    const filters: New_TableFilters = { ...this.scopeFilters(scope), ...this.equalsFilters(equals) }
    return this.signUrlService.generateFilters({
      tiresListFilters: { filters, page: 1, sort },
      tiresListReturnUrl: scope.returnUrl
    })
  }

  /** Tires list restricted to tire units: what the "Tires" cell of every tires summary drills into. */
  tiresListByTireUnits(scope: IDrillDownScope): IUrlFilterItem {
    return this.tiresList(scope, { isTire: true })
  }

  /**
   * Back/front-shop efficiency list opened on one employee alone across the given locations, so its grand total is
   * that employee's efficiency for the period. An empty name part is left unfiltered — the API cannot compare
   * against an empty value.
   */
  employeeEfficiencyList(input: IEmployeeEfficiencyDrillDown): IUrlFilterItem {
    const listFilters: INew_TableAction = {
      page: 1,
      filters: this.equalsFilters({
        ...pickBy({ firstName: input.firstName, lastName: input.lastName }, Boolean),
        locationId: input.locationIds
      }),
      sort: EFFICIENCY_LIST_SORT
    }
    return this.signUrlService.generateFilters(
      input.frontShop
        ? {
            serviceAdvisorListEmployeeId: input.employeeId,
            serviceAdvisorListSelectedDateInfo: input.period,
            serviceAdvisorListFilters: listFilters
          }
        : {
            technicianListEmployeeId: input.employeeId,
            technicianListSelectedDateInfo: input.period,
            technicianListFilters: listFilters
          }
    )
  }

  /** Invoice period, operated-by-LLA and (when narrowed) location filters. */
  private scopeFilters(scope: IDrillDownScope): New_TableFilters {
    const filters: New_TableFilters = {
      invoiceDate: {
        key: 'invoiceDate',
        items: [
          {
            operator: New_TableOperator.Period,
            value: scope.period.periodType,
            extraValue: { from: scope.period.from, to: scope.period.to }
          }
        ]
      },
      operated: {
        key: 'operated',
        items: [{ operator: New_TableOperator.Equals, value: [InvoiceOperatedType.OperatedByLLA] }]
      }
    }
    if (scope.locationIds?.length) {
      filters['locationId'] = {
        key: 'locationId',
        items: [{ operator: New_TableOperator.Equals, value: scope.locationIds }]
      }
    }
    return filters
  }

  private equalsFilters(equals: Record<string, unknown>): New_TableFilters {
    return Object.fromEntries(
      Object.entries(equals).map(([key, value]) => [
        key,
        { key, items: [{ operator: New_TableOperator.Equals, value }] }
      ])
    )
  }
}
